import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

export type JobPostFlow = 'new' | 'edit' | 'repost' | 'draft' | 'paymentDraft';

interface JobPostingSessionState {
  jpf_session_id: string | null;
  jobPostFlow: JobPostFlow | null;
  createSession: (query?: any) => string;
  clearSession: () => void;
  getSessionId: () => string | null;
  getJobPostFlow: () => JobPostFlow | null;
  setJobPostFlow: (flow: JobPostFlow) => void;
  deriveFlowFromQuery: (query: any) => JobPostFlow;
}

export const useJobPostingSessionStore = create<JobPostingSessionState>((set, get) => ({
  jpf_session_id: null,
  jobPostFlow: null,

  deriveFlowFromQuery: (query: any): JobPostFlow => {
    const { job_id, re_post, is_draft, job_post_flow } = query;

    // Check for explicit draft flow parameter FIRST (only for draft case)
    if (job_post_flow === 'draft') {
      return 'draft';
    }
    // Use existing logic for all other cases
    // New job posting (no job_id)
    if (!job_id) {
      return 'new';
    }
    // Repost case (has job_id and re_post=true)
    if (job_id && re_post === 'true') {
      return 'repost';
    }
    // Payment draft case (has job_id and is_draft=true)
    if (job_id && is_draft === 'true') {
      return 'paymentDraft';
    }
    // Regular edit case (has job_id but no other indicators)
    if (job_id) {
      return 'edit';
    }

    return 'new'; // Fallback
  },

  createSession: (query?: any) => {
    const newSessionId = uuidv4();
    const flow = query ? get().deriveFlowFromQuery(query) : 'new';

    set({
      jpf_session_id: newSessionId,
      jobPostFlow: flow
    });

    return newSessionId;
  },

  setJobPostFlow: (flow: JobPostFlow) => {
    set({ jobPostFlow: flow });
  },

  clearSession: () => {
    set({
      jpf_session_id: null,
      jobPostFlow: null
    });
  },

  getSessionId: () => get().jpf_session_id,
  getJobPostFlow: () => get().jobPostFlow
}));
